import * as React from "react";
import { observer, inject } from "mobx-react";

import { Dashboard, DashboardStore } from "Stores/dashboardStore";
import { CommonStore } from "../../stores";
import { Utils } from "../../common/utils";
import { Button, Form, FormCheck, FormControl, FormGroup, Card, Col, Row, Spinner } from "react-bootstrap";
import { toJS } from "mobx";
import { SelectWrapper } from "Components/Common/SelectWrapper";
import PageTitle from "Components/Common/PageTitle";

interface DashboardRegistrationFormProps {
    commonStore?: CommonStore
    dashboardStore?: DashboardStore
}

interface DashboardRegistrationFormState {
    dashboard: Dashboard | undefined | null
    dashboardType: any
}

interface MemoizationObject {
    dashboardOptions: any
}

interface NewDashboard extends Dashboard {
    [key: string]: any;
}

enum DASHBOARD_TYPE {
    TEMPLATE = "TEMPLATE",
    REGISTERED = "REGISTERED",
    NEW = "NEW"
}

const STATUS_OPTIONS = [
    { value: "enabled", label: "Enabled" },
    { value: "disabled", label: "Disabled" },
    { value: "unpublished", label: "Unpublished" }
];

@inject("commonStore", "dashboardStore")
@observer
export class DashboardRegistrationForm extends React.Component<DashboardRegistrationFormProps, DashboardRegistrationFormState> {

    getInitialState = () => ({
        dashboard: this._createDashboard({dashboard_type: "enflux_dashboard"}),
        dashboardType: DASHBOARD_TYPE.NEW,
        selectedDashboard: DASHBOARD_TYPE.NEW,
    });

    state = this.getInitialState();

    memoized: MemoizationObject = {
        dashboardOptions: undefined
    };

    componentDidMount() {
        if (!this.props.dashboardStore) {
            return null;
        }
        this.props.dashboardStore.getDashboardsInCategories(true, true);
        this.props.dashboardStore.getDashboardTemplates();
    }

    handleUrlChange = (ev) => {
        this._updateDashboardState("url", ev.target.value);
    };

    async refreshDashboards() {
        if (!this.props.dashboardStore) {
            return null;
        }
        const _loading = { value: "loading", label: "Loading...", template: false };
        this.memoized.dashboardOptions = [_loading];
        const _preview = document.getElementById("description-preview");
        if (_preview) _preview.innerHTML = "";
        await Promise.all([this.props.dashboardStore.getDashboardsInCategories(true, true),
            this.props.dashboardStore.getDashboardTemplates()]).then(() => {
                this._getOptions(true);
                this.setState(this.getInitialState());
        });
    }

    handleStatusChange = (e: any) => {
        this._updateDashboardState("status", e.value);
    };

    getOptionStyle = {
        option: (styles, { data}) => {
            return {
                ...styles,
                fontWeight: data.template ? "bold" : "normal",
            };
        }
    };

    getDashboardName = obj => obj.label;

    renderButtonPanel() {
        if (!this.props.dashboardStore) {
            return null;
        }
        const buttonStyle: React.CSSProperties = { float: "right", marginLeft: 15 };
        if (this.state.dashboardType === DASHBOARD_TYPE.REGISTERED) {
            return (
                <div style={{ ...buttonStyle }}>
                    <Button size="lg" variant="secondary" id="registration-form-unregister" style={{ minWidth: 207 }} onClick={this._unregisterDashboard} disabled={this.props.dashboardStore.isDashboardProcessing}>
                        {this.props.dashboardStore.isDashboardProcessing ? <Spinner animation={"border"} variant={"secondary"} size="sm"/> : "Unregister"}
                    </Button>
                    <Button size="lg" variant="primary" id="registration-form-update" style={{ marginLeft: 15, minWidth: 207 }} onClick={this._updateDashboard} disabled={!this._isValid() || this.props.dashboardStore.isDashboardProcessing}>
                        {this.props.dashboardStore.isDashboardProcessing ? <Spinner animation={"border"} variant={"secondary"} size="sm"/> : "Update"}
                    </Button>
                </div>
            );
        } else {
            return (
                <Button size="lg" variant="primary" id="registration-form-register" style={{ ...buttonStyle, minWidth: 207 }} onClick={this._registerDashboard} disabled={!this._isValid() || this.props.dashboardStore.isDashboardProcessing}>
                    {this.props.dashboardStore.isDashboardProcessing ? <Spinner animation={"border"} variant={"secondary"} size="sm" /> : "REGISTER"}
                </Button>
            );
        }

    }

    render() {

        const colObject = { span: 6, offset: 3 };
        return (
            <Col>
                <PageTitle pageTitle={"Dashboard Registration"} />
                <Form>
                    <Row>
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-select">
                                <Form.Label>Dashboard</Form.Label>
                                <SelectWrapper
                                    name="dashboard-select"
                                    styles={this.getOptionStyle}
                                    options={this._getOptions(false)}
                                    getOptionValue={this.getDashboardName}
                                    getOptionLabel={this.getDashboardName}
                                    onChange={ this._dashboardSelectionChanged }
                                    value={this.state.dashboard}
                                />
                                <Form.Text className="text-muted">Unregistered dashboards are in bold</Form.Text>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-name">
                                <Form.Label>Name</Form.Label>
                                <FormControl
                                    name="name"
                                    type="text"
                                    required={true}
                                    value={this.state.dashboard.name}
                                    onChange={ this._updateInput }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-status">
                                <Form.Label>Status</Form.Label>
                                <SelectWrapper
                                    name="status"
                                    options={STATUS_OPTIONS}
                                    value={STATUS_OPTIONS.find(obj => obj.value === this.state.dashboard.status)}
                                    onChange={ this.handleStatusChange }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-short-name">
                                <Form.Label>Short Name</Form.Label>
                                <FormControl
                                    name="short_name"
                                    type="text"
                                    required={true}
                                    value={this.state.dashboard.short_name}
                                    onChange={ this._updateInput }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-category">
                                <Form.Label>Category</Form.Label>
                                <FormControl
                                    name="category"
                                    type="text"
                                    required={true}
                                    value={this.state.dashboard.category}
                                    onChange={ this._updateInput }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-description">
                                <Form.Label>Description</Form.Label>
                                <FormControl
                                    name="description"
                                    as="textarea"
                                    required={true}
                                    value={this.state.dashboard.description}
                                    onChange={ this._updateInput }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <Form.Label>Description Preview</Form.Label>
                            <Card><Card.Body id="description-preview"/></Card>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-datasource-name">
                                <Form.Label>Datasource Name</Form.Label>
                                <FormControl
                                    name="datasource_name"
                                    type="text"
                                    required={true}
                                    value={this.state.dashboard.datasource_name}
                                    onChange={ this._updateInput }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <Form.Label>View Permissions</Form.Label>
                            <Card>
                                <Card.Body>
                                    <Row className="text-center">
                                        <Col md={3}>
                                            <FormCheck id="public-can-view" name="public_can_view"
                                                onChange={(e: any) => this._updateCheckbox(e, !this.state.dashboard.public_can_view)}
                                                checked={this.state.dashboard.public_can_view}
                                                label="Public"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <FormCheck id="faculty-can-view" name="faculty_can_view"
                                                onChange={(e: any) => this._updateCheckbox(e, !this.state.dashboard.faculty_can_view)}
                                                checked={this.state.dashboard.faculty_can_view}
                                                label="Faculty"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <FormCheck id="mentors-can-view" name="mentors_can_view"
                                                onChange={(e: any) => this._updateCheckbox(e, !this.state.dashboard.mentors_can_view)}
                                                checked={this.state.dashboard.mentors_can_view}
                                                label="Mentors"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <FormCheck id="admins-can-view" name="admins_can_view"
                                                onChange={(e: any) => this._updateCheckbox(e, !this.state.dashboard.admins_can_view)}
                                                checked={this.state.dashboard.admins_can_view}
                                                label="Admins"
                                            />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-project">
                                <Form.Label>Project</Form.Label>
                                <FormControl
                                    name="project"
                                    type="text"
                                    required={true}
                                    value={this.state.dashboard.project}
                                    onChange={ this._updateInput }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-workbook-name">
                                <Form.Label>Workbook Name</Form.Label>
                                <FormControl
                                    name="workbook_name"
                                    type="text"
                                    required={true}
                                    value={this.state.dashboard.workbook_name}
                                    onChange={ this._updateInput }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-url">
                                <Form.Label>URL</Form.Label>
                                <FormControl
                                    name="url"
                                    type="text"
                                    required={true}
                                    value={this.state.dashboard.url}
                                    onChange={ this.handleUrlChange }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row className="mt-2 mb-2">
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-is-responsive">
                                <Form.Check // prettier-ignore
                                    type="checkbox"
                                    id="dashboard-is-responsive"
                                    name="is_responsive"
                                    checked={this.state.dashboard.is_responsive}
                                    onChange={(e: any) => this._updateCheckbox(e, !this.state.dashboard.is_responsive)}
                                    label="Is Responsive"
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-published-date">
                                <Form.Label>Published</Form.Label>
                                <FormControl
                                    name="published_date"
                                    type="text"
                                    required={true}
                                    value={this._parseDate(this.state.dashboard.published_date)}
                                    disabled
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={colObject}>
                            <FormGroup controlId="dashboard-updated">
                                <Form.Label>Updated</Form.Label>
                                <FormControl
                                    name="updated"
                                    type="text"
                                    required={true}
                                    value={this._parseDate(this.state.dashboard.updated)}
                                    disabled
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row style={{ paddingTop: 10 }}>
                        <Col md={colObject}>
                            {this.renderButtonPanel()}
                        </Col>
                    </Row>
                </Form>
            </Col>
        );
    }

    private _isValid = () => {
        const { dashboard } = this.state;

        return dashboard.name && dashboard.short_name && dashboard.category &&
         dashboard.description && dashboard.datasource_name && dashboard.project && dashboard.workbook_name;
    };

    private _getOptions = (skipMemoized?: boolean) => {
        if (!this.props.dashboardStore) {
            return null;
        }
        if (!this.props.dashboardStore.dashboardsInCatgories || !this.props.dashboardStore.dashboardTemplates) return;

        if (this.memoized.dashboardOptions && !skipMemoized) return this.memoized.dashboardOptions;

        const _new = [{ type: `${DASHBOARD_TYPE.NEW}`, index: -1, label: "New Dashboard", template: false }];
        const _registered = this.props.dashboardStore.dashboards.map((item, index) => {
            return { type: `${DASHBOARD_TYPE.REGISTERED}`, index: index, id: item.id, name: item.name, label: item.name, template: false };
        });
        const _templates = toJS(this.props.dashboardStore.dashboardTemplates).map((item, index) => {
            return { type: `${DASHBOARD_TYPE.TEMPLATE}`, index: index, name: item.name, label: item.name, template: true };
        });
        this.memoized.dashboardOptions = _new.concat(_templates.sort(this._sort), _registered.sort(this._sort));
        return this.memoized.dashboardOptions;
    };

    private _sort = (a: any, b: any) => {
        if (a.name < b.name) { return -1; }
        if (a.name > b.name) { return 1; }
        return 0;
    };

    private _parseDate = (date: string | undefined) => {
        if (date === undefined || date === "") return "";

        return Utils.getValidDateFromString(date, "MM/DD/YY h:mm A");
    };

    private _dashboardSelectionChanged = (selectedOption: any) => {
        if (!this.props.dashboardStore) {
            return null;
        }
        let _dashboard;
        let _dashboardType = DASHBOARD_TYPE.NEW;
        switch(selectedOption.type) {
            case DASHBOARD_TYPE.TEMPLATE:
                _dashboardType = DASHBOARD_TYPE.TEMPLATE;
                _dashboard = toJS(this.props.dashboardStore.dashboardTemplates)[selectedOption.index];
                break;
            case DASHBOARD_TYPE.REGISTERED:
                _dashboardType = DASHBOARD_TYPE.REGISTERED;
                _dashboard = this.props.dashboardStore.dashboards.find(item => item.id === selectedOption.id);
                break;
            default:
                _dashboardType = DASHBOARD_TYPE.NEW;
        }
        _dashboard = this._createDashboard({label: selectedOption.label, dashboard_type: "enflux_dashboard", ..._dashboard});

        if (_dashboard.description) this._updatePreview(_dashboard.description);

        this.setState({
            dashboard: _dashboard,
            dashboardType: _dashboardType,
        });
    };

    private _updatePreview = (html: string) => {
        const _preview = document.getElementById("description-preview");
        if (_preview) _preview.innerHTML = html;
    };

    private _updateCheckbox = (event: any, value: boolean) => {
        const { target: { name } } = event;
        this._updateDashboardState(name, value);
    };

    private _updateInput = (event: any) => {
        const { target: { name, value } } = event;
        this._updateDashboardState(name, value);
    };

    private _updateDashboardState = (name: string, value: any) => {
        const _dashboard = this.state.dashboard;
        _dashboard[name] = value;
        this.setState({ dashboard: _dashboard});

        if (name === "description") this._updatePreview(value);
    };

    private _createDashboard(data: any): NewDashboard {
        const _handler = {
            get: function(obj: any, prop: string) {
                switch(prop) {
                    case "status":
                        return this.defaultVal(obj, prop, "enabled");
                    case "project":
                        return this.defaultVal(obj, prop, "Default");
                    case "id":
                        return this.defaultVal(obj, prop, -1);
                    case "datasource_name": {
                        let _datasourceName = null;
                        if (obj.hasOwnProperty(prop)) {
                            _datasourceName = obj[prop];
                        } else if (obj.hasOwnProperty("datasource")) {
                            _datasourceName = obj["datasource"] == null ? "" : obj["datasource"].datasource_name;
                        }
                        return _datasourceName == null ? "" : _datasourceName;
                    }
                    case "faculty_can_view":
                    case "mentors_can_view":
                    case "public_can_view":
                    case "admins_can_view":
                    case "is_responsive":
                        return this.defaultVal(obj, prop, false);
                    default:
                        return this.defaultVal(obj, prop, "");
                }
            },
            defaultVal: function(obj: any, prop: string, defaultVal: any) {
                return obj.hasOwnProperty(prop) && obj[prop] != null ? obj[prop] : defaultVal;
            }
        };
        const _newDashboard = data === null ? {} : data;
        return new Proxy(_newDashboard, _handler);
    }

    private _registerDashboard = () => {
        if (!this.props.dashboardStore) {
            return null;
        }
        this.props.dashboardStore.registerDashboard(this.state.dashboard).then(() => {
            this._cleanup("registered");
        }, (reason: any) => {
        });
    };

    private _updateDashboard = () => {
        if (!this.props.dashboardStore) {
            return null;
        }
        this.props.dashboardStore.updateDashboard(this.state.dashboard).then(() => {
            this._cleanup("updated");
        }, (reason: any) => {
        });
    };

    private _unregisterDashboard = () => {
        if (!this.props.dashboardStore) {
            return null;
        }
        this.props.dashboardStore.unregisterDashboard(this.state.dashboard.id).then(() => {
            this._cleanup("unregistered");
        }, (reason: any) => {
        });
    };

    private _cleanup = async (action: string) => {
        if (!this.props.commonStore) {
            return null;
        }
        this.props.commonStore.addAlert(`Successfully ${action} ${this.state.dashboard.name}`);
        this.refreshDashboards();
    };
}